import { ChartFareBreakup } from "src/context/chartsContext/chartContext/types";
import { getCookie, isEmpty, isWindowPresent } from "./browserHelpers";
import { getFareInitialObj } from "src/context/chartsContext/chartContext/store";
import xss from "xss";
import { BannerData, BannerList } from "src/context/globalStateContext/types";
import {
  FareBreakupState,
  Pricing,
  Seat,
} from "src/containers/itinerary/types";

export const path = (p: any, o: any) => {
  const reducerFunction = (xs: any, x: any) => {
    return xs && xs[x] ? xs[x] : null;
  };
  return p.reduce(reducerFunction, o);
};

export function getUserAuthValues() {
  const [email, profileName, gender, photo, userId] = decodeURIComponent(
    getCookie("userid") || ""
  ).split("|");
  return {
    email,
    profileName,
    gender,
    photo,
    userId,
  };
}

export const isUserSignedIn = () => {
  const userObject = getUserAuthValues() || {};
  const usermiscVal = decodeURIComponent(getCookie("usermisc") || "").split(
    "|"
  );
  const signedIn =
    usermiscVal.includes("SIGNED_IN") &&
    userObject.userId &&
    userObject.userId.length > 0
      ? true
      : false;

  return signedIn;
};

export const formatFare = (fare: number | string) => {
  return fare.toLocaleString("en-US");
};

export const formatNumber = (number) => {
  const floatNumber = parseFloat(number);
  const formattedNumber = parseFloat(floatNumber.toFixed(2));
  return formattedNumber;
};

export const getSearchParams = (): Record<string, string> => {
  if (isWindowPresent) {
    const param =
      decodeURIComponent(window.location.search).split("?").length > 1
        ? decodeURIComponent(window.location.search).split("?")[1]
        : false;
    const urlParamsStrArr = param ? param.split("&") : [];
    let urlParams = {};
    urlParamsStrArr.map((value) => {
      const paramArr = value.split("=");
      urlParams = {
        ...urlParams,
        [paramArr[0]]: value.substring(paramArr[0].length + 1),
      };
    });
    return urlParams;
  } else {
    return {};
  }
};

export function jsonToQueryString(jsonObject: Record<string, string>) {
  let queryString = "";
  for (const [key, value] of Object.entries(jsonObject)) {
    queryString += `&${key}=${value}`;
  }
  return queryString.substring(1);
}

export const generateUniqueArray = (array1: unknown[], array2: unknown[]) => {
  return [...new Set([...array2, ...array1])];
};

export const getKeyByValue = (object: Record<string, unknown>, value: any) => {
  return Object.keys(object).find((key) => {
    if (JSON.stringify(object[key]) === JSON.stringify(value)) return key;
  });
};

export const sortAlphabeticalOrder = (
  a: any,
  b: any,
  propertyName = "title"
) => {
  if (a[propertyName] < b[propertyName]) {
    return -1;
  }
  if (a[propertyName] > b[propertyName]) {
    return 1;
  }
  return 0;
};

export const generateFareBreakup = (seats: Seat[]) => {
  const fareObj: FareBreakupState = {
    fare: getFareInitialObj(0),
    fareCategory: getFareInitialObj(),
    fareLabel: getFareInitialObj(),
    seats: 0,
  };

  seats.forEach((item: Seat) => {
    fareObj.seats++;
    let pricing: Pricing;

    if (item.traveller?.type) {
      pricing =
        item.traveller?.type === "ADT"
          ? item?.pricing || {}
          : item?.childPricing || {};
    } else {
      pricing = item?.pricing || {};
    }

    for (const key of Object.keys(pricing)) {
      fareObj.fare[key] = (fareObj.fare[key] || 0) + pricing[key].amount;
      fareObj.fareCategory[key] = pricing[key].category;
      fareObj.fareLabel[key] = pricing[key].label as string;
    }
  });

  return fareObj;
};

export const generateFareBreakupForPB = (pricingArray, seats) => {
  const fare = {};
  const fareCategory = {};
  const fareLabel = {};

  pricingArray.forEach((item) => {
    const { amount, code, label } = item;

    if (label === "CB") {
      const absAmount = Math.abs(amount);

      if ("DIS" in fare) {
        fare["DIS"] += absAmount;
      } else {
        fare["DIS"] = absAmount;
      }

      fareCategory["DIS"] = "DIS";
      fareLabel["DIS"] = `Coupon code (${code})`;
    } else if (code === "DIS" && label === "Discount") {
      // Add the "DIS" item to the "SUP_DIS" category
      if ("SUP_DIS" in fare) {
        fare["SUP_DIS"] += amount;
      } else {
        fare["SUP_DIS"] = amount;
      }

      fareCategory["SUP_DIS"] = "DIS";
      fareLabel["SUP_DIS"] = "Discount";
    } else {
      if (code in fare) {
        fare[code] += amount;
      } else {
        fare[code] = amount;
      }

      fareCategory[code] = code;
      fareLabel[code] = label;
    }
  });

  return {
    fare,
    fareCategory,
    fareLabel,
    seats,
  };
};

export const addNewFareItem = (
  fareBreakup: ChartFareBreakup,
  amount: number,
  category: string,
  label: string
) => {
  const updateFare = { ...fareBreakup };
  updateFare.fare = { ...updateFare.fare, [category]: amount };
  updateFare.fareCategory = {
    ...updateFare.fareCategory,
    [category]: category,
  };
  updateFare.fareLabel = {
    ...updateFare.fareLabel,
    [category]: label,
  };
  return updateFare;
};

export const santizeHTML = (value: string | number) => {
  return xss(value.toString(), {
    whiteList: {}, // empty, means filter out all tags
    stripIgnoreTag: true, // filter out all HTML not in the whilelist
    stripIgnoreTagBody: ["script"], // the script tag is a special case, we need
    // to filter out its content
  });
};
export const onlyNumbersRegex = /^[0-9]+$/;
export const validateMobileNumber = (
  mobile: string,
  minLength: number,
  maxLength: number
) => {
  if (
    mobile &&
    mobile.length >= minLength &&
    mobile.length <= maxLength &&
    onlyNumbersRegex.test(mobile)
  ) {
    return true;
  }
  return false;
};
export const getNestedField = (fieldArray: any, objectToTraverse: any) => {
  const reducerFunction = (accumalator: any, current: any) => {
    return accumalator && accumalator[current] ? accumalator[current] : null;
  };
  return fieldArray.reduce(reducerFunction, objectToTraverse);
};
export const isUserSignedInServerSide = (
  cookies: Partial<{ [key: string]: string }>
) => {
  const [email, profileName, gender, photo, userId] = decodeURIComponent(
    cookies["userid"] || ""
  ).split("|");
  const userObject = {
    email,
    profileName,
    gender,
    photo,
    userId,
  };
  const usermiscVal = decodeURIComponent(cookies["usermisc"] || "").split("|");
  const signedIn =
    usermiscVal.includes("SIGNED_IN") &&
    userObject.userId &&
    userObject.userId.length > 0
      ? true
      : false;
  return signedIn;
};

export const CURRENCY = "INR";
export const CURRENCYLOCAL = "en-IN";

export const CURRENCIES = {
  INR: "INR",
} as const;

export const CURRENCY_TO_SYMBOL = {
  [CURRENCIES.INR]: "₹",
} as const;

export const formatCurrency = (
  value: string | number,
  currencyCode?: string
) => {
  if (!value) return value;

  if (typeof value === "string") {
    value = value.replace(/,/g, "");
  }

  if (typeof value === "number") {
    value = value.toString();
  }

  const formatted = parseInt(value, 10).toLocaleString(CURRENCYLOCAL, {
    style: "currency",
    currency: currencyCode || CURRENCY,
    minimumFractionDigits: 0,
  });
  return formatted;
};

export const getGenderForTraveller = (type: string) => {
  const title = type.toLowerCase();

  switch (title) {
    case "mr":
    case "male":
    case "m":
      return "M";
    case "mrs":
    case "female":
    case "f":
      return "F";
  }
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
export const getPagePath = (
  pagePath: string | number | undefined,
  pathname: string[] | undefined
) => {
  return pagePath === "results"
    ? "SRP"
    : !pagePath
    ? "HP"
    : pathname != undefined &&
      pathname.length > 4 &&
      pathname[4] === "info" &&
      pathname[2] === "itinerary"
    ? "Itinerary"
    : "Details_Page";
};

export const getQueryParamString = (obj: Record<string, string>) => {
  const str = [];
  for (const p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }

  return str.join("&");
};

export const handleDisabledDates = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  // Disable dates that are before today
  return date < today;
};

export const PaymentType = {
  CC: "credit card",
  DC: "detbit card",
  NB: "net banking",
  TC: "tech process",
  DA: "deposit account",
  WT: "cleartrip wallet",
  TW: "third party wallet",
  GV: "gift voucher",
  UP: "UPI",
  AP: "Apple Pay",
  RP: "reward points",
  EMI: "EMI",
  PL: "Paylater",
  CL_EMI: "cardless emi",
  CS: "credit shell",
};

export const getBannersForToday = (data: BannerData[]) => {
  let bannerList: BannerList | Record<string, unknown> = {};
  const currentTime = new Date();

  const currentOffset = currentTime.getTimezoneOffset();

  const ISTOffset = 330; // IST offset UTC +5:30

  const ISTTime = new Date(
    currentTime.getTime() + (ISTOffset + currentOffset) * 60000
  );

  for (let index = 0; index < data.length; index++) {
    const config = data[index];
    if (
      config.mobileBannerList &&
      new Date(config.time.from) <= ISTTime &&
      new Date(config.time.to) >= ISTTime
    ) {
      bannerList = { ...config.mobileBannerList };
      break;
    }
  }
  return bannerList;
};

export const checkInternalCTUser = () => {
  const usermisc = getCookie("usermisc") || "";
  const { email: signedInEmail = "" } = getUserAuthValues();
  if (
    usermisc.includes("SIGNED_IN") &&
    !isEmpty(signedInEmail) &&
    signedInEmail.indexOf("cleartrip.com") > -1
  ) {
    return true;
  }
  return false;
};

export async function encryptToSHA256(str?: string) {
  // Convert the string to a Uint8Array
  if (str) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);

    // Calculate the SHA-256 hash
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    // Convert the hash buffer to a hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");

    return hashHex;
  } else return undefined;
}
